import React, { FormEvent, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowRightIcon, CheckIcon } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { ParallaxImage } from "../components/ParallaxImage";
import { IMAGES } from "../data/images";
import { EASE } from "../utils/motion";
type Category = "All" | "News" | "Events" | "Achievements";
type Story = {
  category: Exclude<Category, "All">;
  title: string;
  date: string;
  summary: string;
  image: string;
  status?: string;
};

// Published entries are managed in the CMS. Only verified items appear here.
const STORIES: Story[] = [{
  category: "Events",
  title: "Setting up your child for success abroad with the OSSD",
  date: "1 April 2023",
  summary: "A parent seminar at Maple Canadian College, Lekki, introducing the Ontario Secondary School Diploma pathway and the MCC entrance exam.",
  image: IMAGES.campus,
  status: "Past event"
}];
const CATEGORIES: Category[] = ["All", "News", "Events", "Achievements"];
export function News() {
  const [filter, setFilter] = useState<Category>("All");
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const reduce = useReducedMotion();
  const visible = STORIES.filter(s => filter === "All" || s.category === filter);
  const featured = STORIES[0];
  function subscribe(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email.trim()) return;
    setSubscribed(true);
  }
  return <>
      <PageHero variant="editorial" eyebrow="NEWS & EVENTS" title="The MCC Journal." description="School news, events and student achievements, published when they’re ready to share." breadcrumb="News & Events" data-id="element-1383" data-mp="2qbucn5nv2tnx" />

      <main data-id="element-1384" data-mp="2h8t839en9amd">
        <section className="mx-auto max-w-[1440px] px-5 pb-24 md:px-9 md:pb-32" data-id="element-1385" data-mp="2h9b9slk2sh1o">
          <article className="grid gap-10 border-t border-[#061f38] pt-10 lg:grid-cols-12" data-id="element-1386" data-mp="2cccg244bqc40">
            <div className="lg:col-span-7" data-id="element-1387" data-mp="22mcoz9fq4kvx">
              <ParallaxImage src={featured.image} alt={featured.title} className="aspect-[16/10]" priority data-id="element-1388" data-mp="22ps0y3e3pdt4" />
            </div>
            <Reveal className="flex flex-col justify-end lg:col-span-5" data-id="element-1389" data-mp="1sprew9oukjl8">
              <div className="flex items-center gap-3 text-xs font-semibold tracking-[0.14em]" data-id="element-1390" data-mp="1srkonxurbjxg">
                <span className="text-[#bb1e35]" data-id="element-1391" data-mp="1xq5ohw63k1ld">{featured.category.toUpperCase()}</span>
                <span className="text-[#9aa1a6]" data-id="element-1392" data-mp="2lg006aeojk41">·</span>
                <span className="text-[#6b7378]" data-id="element-1393" data-mp="2qbyibg9f05w0">{featured.date}</span>
              </div>
              <h2 className="mt-5 text-4xl font-medium leading-[0.98] tracking-[-0.055em] md:text-5xl" data-id="element-1394" data-mp="2bovl7ap07z97">{featured.title}</h2>
              <p className="mt-5 max-w-md text-base leading-7 text-[#4f5963]" data-id="element-1395" data-mp="2glvqhje14xz9">{featured.summary}</p>
              {featured.status && <span className="mt-6 w-fit border border-[#cfcac0] px-3 py-1.5 text-xs text-[#6b7378]" data-id="element-1396" data-mp="34aogkz7z0bly">{featured.status}</span>}
            </Reveal>
          </article>
        </section>

        <section className="border-t border-[#dedbd3] bg-[#f2f0eb] py-20 md:py-28" data-id="element-1397" data-mp="xmxx97nkggdm">
          <div className="mx-auto max-w-[1440px] px-5 md:px-9" data-id="element-1398" data-mp="somlushmhxta">
            <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end" data-id="element-1399" data-mp="16gkockgtwvgh">
              <h2 className="text-4xl font-medium tracking-[-0.06em] md:text-5xl" data-id="element-1400" data-mp="12fh9a06lv3gb">All stories</h2>
              <div role="tablist" aria-label="Filter stories" className="flex flex-wrap gap-1" data-id="element-1401" data-mp="epmu3bytd2sw">
                {CATEGORIES.map(c => <button key={c} role="tab" aria-selected={filter === c} onClick={() => setFilter(c)} className={`relative px-4 py-2 text-sm transition-colors duration-200 ${filter === c ? "text-white" : "text-[#4f5963] hover:text-[#061f38]"}`} data-id="element-1402" data-mp="9rv6uv9464v1">
                    {filter === c && <motion.span layoutId="news-filter" transition={{
                  duration: reduce ? 0 : 0.35,
                  ease: EASE
                }} className="absolute inset-0 bg-[#061f38]" data-id="element-1403" data-mp="1z583rzewcqm4" />}
                    <span className="relative" data-id="element-1404" data-mp="1u65a1b4da6tg">{c}</span>
                  </button>)}
              </div>
            </div>

            <div className="mt-10 min-h-[200px] border-t border-[#061f38]" data-id="element-1405" data-mp="2ufema5kj2op2">
              <AnimatePresence mode="wait" data-id="element-1406" data-mp="2uhhn7kn7y28k">
                <motion.div key={filter} initial={{
                opacity: 0,
                y: reduce ? 0 : 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0
              }} transition={{
                duration: reduce ? 0.01 : 0.35,
                ease: EASE
              }} data-id="element-1407" data-mp="2zehrbq0icux1">
                  {visible.length > 0 ? visible.map(s => <article key={s.title} className="grid gap-6 border-b border-[#cfcac0] py-8 md:grid-cols-[200px_1fr_140px] md:items-center" data-id="element-1408" data-mp="3de0mzjc4udsg">
                        <img src={s.image} alt="" aria-hidden="true" loading="lazy" className="aspect-[4/3] w-full object-cover" data-id="element-1409" data-mp="3i60txvqme6yj" />
                        <div data-id="element-1410" data-mp="3s3ex4tnj8z8w">
                          <p className="text-xs font-semibold tracking-[0.14em] text-[#bb1e35]" data-id="element-1411" data-mp="3s4gc397o65vw">{s.category.toUpperCase()}</p>
                          <h3 className="mt-2 text-2xl font-medium tracking-[-0.04em]" data-id="element-1412" data-mp="5nskoaf50g">{s.title}</h3>
                        </div>
                        <p className="text-sm text-[#6b7378] md:text-right" data-id="element-1413" data-mp="1kfc0184xnl7s">{s.date}</p>
                      </article>) : <div className="py-16" data-id="element-1414" data-mp="1bhiduq0vu7yc">
                      <p className="text-2xl font-medium tracking-[-0.04em]" data-id="element-1415" data-mp="16hvstuetbnez">Nothing published in {filter} yet.</p>
                      <p className="mt-2 text-sm text-[#4f5963]" data-id="element-1416" data-mp="2465rbzfe3t1c">New stories appear here as soon as MCC publishes them. Subscribe below to hear first.</p>
                    </div>}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </section>

        <section className="bg-[#061f38] py-24 text-white md:py-32" data-id="element-1417" data-mp="1phjfw8svvlfe">
          <div className="mx-auto grid max-w-[1440px] gap-10 px-5 md:px-9 lg:grid-cols-12 lg:items-end" data-id="element-1418" data-mp="2mw5fmk3ep43p">
            <Reveal className="lg:col-span-6" data-id="element-1419" data-mp="2i5p0mvjxtaf8">
              <p className="text-xs font-semibold tracking-[0.18em] text-[#f0b1b9]" data-id="element-1420" data-mp="2i3uejdwxjc9k">STAY IN TOUCH</p>
              <h2 className="mt-5 text-4xl font-medium leading-[0.95] tracking-[-0.06em] md:text-6xl" data-id="element-1421" data-mp="g8eucmzfyysq">Keep me up to date with MCC.</h2>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-5 lg:col-start-8" data-id="element-1422" data-mp="6ibty1pzqikd">
              {subscribed ? <p role="status" className="flex items-center gap-3 border-b border-white/30 py-4 text-lg" data-id="element-1423" data-mp="bgmxxvtltyuo"><CheckIcon size={18} className="text-[#f0b1b9]" data-id="element-1424" data-mp="3spju8pm1zz9j" /> You’re on the list. Thank you.</p> : <form onSubmit={subscribe} className="flex border-b border-white/40 focus-within:border-white" data-id="element-1425" data-mp="1k1uvg15axth">
                  <label htmlFor="news-email" className="sr-only" data-id="element-1426" data-mp="3jpwz2ljx3pox">Email address</label>
                  <input id="news-email" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="Your email address" className="w-full bg-transparent py-4 text-lg text-white outline-none placeholder:text-white/40" data-id="element-1427" data-mp="3ooq3zpl528dh" />
                  <button type="submit" className="inline-flex shrink-0 items-center gap-2 px-2 text-sm font-semibold hover:text-[#f0b1b9]" data-id="element-1428" data-mp="1mtz7mnc1pbor">Subscribe <ArrowRightIcon size={16} data-id="element-1429" data-mp="1s1p6mr5wbt63" /></button>
                </form>}
            </Reveal>
          </div>
        </section>
      </main>
    </>;
}